import { Button, IconButton, Tooltip } from '@mui/material';
import {
  BytesPerMegaByte,
  DEFAULT_ACCEPTED_ATTACHMENT_TYPES,
  MAX_ATTACHMENT_FILES_PER_SELECTION,
  UploadMaxFileSizes,
} from '@tempo-io/fim3-dto';
import { useConfirmationDialog } from '@tempo-io/roadmapping-frontend-confirm-dialog';
import { AddCircle, AttachFile, Delete, Download, PictureAsPdf, UploadFile } from '@tempo-io/sui-icons';
import { type ChangeEvent, type DragEvent, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useSetSnackbarMessage } from '../../context/SnackbarContext';
import { getSafeName, getSafeUrl } from '../../utils/Utilities';
import { isAcceptedFileType } from './fileValidation';
import * as Styled from './Styled';

export type AttachmentBase = {
  name: string;
  id: number;
  createdAt: Date;
  attachmentKey: string;
  attachmentUrl: string;
  mimeType: string;
  size: number;
};

export type AttachmentsProps = {
  hideTitle?: boolean;
  attachments: AttachmentBase[];
  updateAttachment?: (file: File, filePath: string) => void;
  deleteAttachment?: (id: number) => void;
  disabled?: boolean;
  acceptedFileTypes?: string[];
};

const Attachment = ({
  attachment,
  deleteAttachment,
  disabled,
}: {
  attachment: AttachmentBase;
  deleteAttachment?: (id: number) => void;
  disabled?: boolean;
}) => {
  const { t } = useTranslation('translation');

  const localUrl = attachment?.attachmentUrl;
  const { openDialog, closeDialog } = useConfirmationDialog();

  const safeUrl = getSafeUrl(localUrl);
  const safeName = getSafeName(attachment.name);

  let thumbnail = <AttachFile size={44} />;
  switch (attachment.mimeType) {
    case 'application/pdf':
      thumbnail = <PictureAsPdf size={44} />;
      break;
    default:
      if (attachment.mimeType.startsWith('image/') && safeUrl) {
        thumbnail = <img src={safeUrl} alt={safeName} />;
      }
  }

  const deleteImage = async () => {
    if (!deleteAttachment || !attachment || !attachment?.attachmentUrl) return;
    deleteAttachment(attachment.id);
  };

  return (
    <Styled.Attachment>
      <Styled.ImageContainer
        className="fs-exclude"
        data-dd-action-name="FIM 3 Attactment - thumbnail redacted"
      >
        {' '}
        {thumbnail}
      </Styled.ImageContainer>
      <p
        className="fs-exclude text-sm leading-normal grow"
        data-dd-action-name="FIM 3 Attactment - name redacted"
      >
        {safeName}
      </p>
      <div>
        {safeUrl && (
          <a href={safeUrl} target="blank">
            <IconButton size="small">
              <Download className="size-6" />
            </IconButton>
          </a>
        )}
        {deleteAttachment && !disabled && (
          <IconButton
            size="small"
            onClick={() => {
              openDialog({
                title: t(($) => $.intake.general.deleteAttachment),
                content: t(($) => $.intake.general.areYouSureYouWantToDeleteThisAttachment),
                showCloseButton: true,
                actionsSlot: (
                  <>
                    <Button className="lowercase" onClick={closeDialog}>{t(($) => $.common.cancel)}</Button>
                    <Button
                      data-testid="fim3-confirm-delete-attachment"
                      color="error"
                      variant="contained"
                      onClick={() => {
                        deleteImage();
                        closeDialog();
                      }}
                    >
                      {t(($) => $.intake.general.delete)}
                    </Button>
                  </>
                ),
                onDialogClose: closeDialog,
              });
            }}
          >
            <Delete className="size-6" />
          </IconButton>
        )}
      </div>
    </Styled.Attachment>
  );
};

export const Attachments = ({
  hideTitle,
  attachments,
  updateAttachment,
  deleteAttachment,
  disabled,
  acceptedFileTypes = DEFAULT_ACCEPTED_ATTACHMENT_TYPES,
}: AttachmentsProps) => {
  const { t } = useTranslation('translation');
  const { t: a11y } = useTranslation('translation');

  const setSnackbarMessage = useSetSnackbarMessage();

  const [isDragging, setDragging] = useState(false);

  const formattedFileTypes = acceptedFileTypes
    .map(ext => ext.replace('.', '').toUpperCase())
    .join(', ')
    .replace(/, ([^,]*)$/, ' or $1');

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(true);
  };

  const handleDragExit = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(false);
  };

  const handleDrop = async (event: DragEvent<HTMLDivElement>) => {
    handleDragExit(event);

    if (!event.dataTransfer.files) return;

    await handleFiles(Array.from(event.dataTransfer.files));
  };

  const getFileError = (file: File): string | undefined => {
    if (!isAcceptedFileType(file, acceptedFileTypes)) {
      return t(($) => $.intake.general.fileTypeNotAccepted, {
        acceptedFileTypes: formattedFileTypes,
      });
    }
    if (file.size > UploadMaxFileSizes.attachment) {
      return t(($) => $.intake.general.fileExceedsMaximumSize, {
        maxSize: UploadMaxFileSizes.attachment / BytesPerMegaByte,
      });
    }
    return undefined;
  };

  const handleFiles = async (files: File[]) => {
    if (!updateAttachment || files.length === 0) return;

    if (files.length > MAX_ATTACHMENT_FILES_PER_SELECTION) {
      setSnackbarMessage({
        message: t(($) => $.intake.general.tooManyFilesSelected, {
          max: MAX_ATTACHMENT_FILES_PER_SELECTION,
        }),
        severity: 'error',
      });
      return;
    }

    const rejectedNames: string[] = [];
    for (const file of files) {
      const error = getFileError(file);
      if (error) {
        rejectedNames.push(file.name);
        continue;
      }
      updateAttachment(file, file.name);
    }

    if (rejectedNames.length > 0) {
      setSnackbarMessage({
        message: t(($) => $.intake.general.filesCouldNotBeAdded, {
          count: rejectedNames.length,
          names: rejectedNames.join(', '),
          acceptedFileTypes: formattedFileTypes,
        }),
        severity: 'error',
      });
    }
  };

  const handleInputChange = async (event: ChangeEvent<HTMLInputElement>) => {
    if (!updateAttachment || !event.target.files) return;

    await handleFiles(Array.from(event.target.files));

    event.target.value = '';
  };

  return (
    <Styled.Container>
      <Styled.TitleContainer sx={{
        display: hideTitle ? 'none' : 'inherit',
      }}>
        <Styled.Title className="capitalize">{!hideTitle && t(($) => $.intake.general.attachments)}</Styled.Title>
        {updateAttachment && !disabled && (
          <Tooltip title={t(($) => $.intake.general.uploadAttachments)}>
            <IconButton size="small">
              <AddCircle className="text-icon-info" />
              <Styled.HiddenUploadInput
                aria-label={a11y(($) => $.intake.a11y.uploadFile)}
                accept={acceptedFileTypes.join(',')}
                multiple
                type="file"
                onChange={handleInputChange}
                data-testid="file-input"
              />
            </IconButton>
          </Tooltip>
        )}
      </Styled.TitleContainer>
      {updateAttachment && !disabled && (
        <Styled.AttachmentsContainer
          onDragOver={handleDragOver}
          onDragLeave={handleDragExit}
          onDrop={handleDrop}
          isDragging={isDragging}
        >
          <Styled.HiddenUploadInput
            aria-label={a11y(($) => $.intake.a11y.uploadFileArea)}
            accept={acceptedFileTypes.join(',')}
            multiple
            type="file"
            onChange={handleInputChange}
            data-testid="file-input-drag-drop-area"
          />
          <Styled.UploadFileInstructionsContainer>
            <IconButton aria-label={a11y(($) => $.intake.a11y.uploadFileButton)}>
              <UploadFile className="text-icon-neutral-primary size-6" />
              <Styled.HiddenUploadInput
                aria-label={a11y(($) => $.intake.a11y.uploadFileButton)}
                accept={acceptedFileTypes.join(',')}
                multiple
                type="file"
                onChange={handleInputChange}
                data-testid="file-input-drag-drop-button"
              />
            </IconButton>
            <p className="text-sm leading-normal font-normal">
              <Trans i18nKey="intake.general.clickToUploadOrDragAndDrop"
                components={{
                  span: <span className="underline" />
                }} />
            </p>
          </Styled.UploadFileInstructionsContainer>
          <Styled.SubText>
            {t(($) => $.intake.general.upload.supportedFileTypesAndSize, {
              acceptedFileTypes: formattedFileTypes,
              maxSize: UploadMaxFileSizes.attachment / BytesPerMegaByte,
            })}
          </Styled.SubText>
        </Styled.AttachmentsContainer>
      )}
      {attachments.map((attachement) => (
        <Attachment
          disabled={disabled}
          key={attachement.id}
          attachment={attachement}
          deleteAttachment={deleteAttachment}
        />
      ))}
    </Styled.Container>
  );
};
